import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteSection, updateSectionsOrder, updateSectionTitle } from '../api';
import type { SectionOrderItem } from '../types/myStore.types';
import { stanNotify } from '@/lib';
import { QUERY_KEYS } from '@/lib/constants';

export function useUpdateSectionsOrder(storeId: number) {
    return useMutation({
        mutationFn: (sections: SectionOrderItem[]) => updateSectionsOrder(storeId, sections),
        onError: (error: any) => {
            stanNotify({
                type: 'error',
                title: 'Update Failed',
                text: error.response?.data?.message || 'Failed to update section order',
            });
        },
    });
}

export function useUpdateSectionTitle(storeId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ sectionId, title }: { sectionId: number; title: string }) =>
            updateSectionTitle(storeId, sectionId, title),
        onSuccess: () => {
            stanNotify({
                type: 'success',
                title: 'Section Updated',
                text: 'Section title has been updated successfully',
            });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STORE_DATA] });
        },
        onError: (error: any) => {
            stanNotify({
                type: 'error',
                title: 'Update Failed',
                text: error.response?.data?.message || 'Failed to update section title',
            });
        },
    });
}

export function useDeleteSection(storeId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (sectionId: number) => deleteSection(storeId, sectionId),
        onSuccess: () => {
            stanNotify({
                type: 'success',
                title: 'Section Deleted',
                text: 'Section has been deleted successfully',
            });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STORE_DATA] });
        },
        onError: (error: any) => {
            stanNotify({
                type: 'error',
                title: 'Delete Failed',
                text: error.response?.data?.message || 'Failed to delete section',
            });
        },
    });
}
